/* Dataset → Explorer: up to five comparison panels over a shared time window.
 *
 * Navigation copies the Frequency view's `.toolbar-tab` presets, but NOT its
 * rangeslider: over a 40-year span the handle for a one-day window is a couple
 * of pixels wide, impossible to grab, and dragging its edges silently changes
 * the interval. Here the interval is set only by the presets and the date
 * pickers; a single fixed-width scrub bar slides that interval along the span.
 *
 * Three traps this file is written around:
 *   - Plotly latches height 0 if it mounts into a collapsed box, so every
 *     panel carries an explicit pixel height in both the layout and the style.
 *   - uirevision keyed on the window, not a constant: a constant would make
 *     Plotly ignore the programmatic range change from a preset button, while
 *     no uirevision at all would throw away the user's own zoom on refetch.
 *   - a popup opened after an await is blocked, so the full-screen tab is
 *     opened synchronously inside the click handler and written to afterwards.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Plot from 'react-plotly.js';
import {
  API, NATURES, NATURE_IDS, NatureChip, PALETTE, SeriesTree,
  PANEL_BG, PANEL_BG_PLOT, PANEL_BORDER, PANEL_GRID, PANEL_TEXT, PANEL_TEXT_DIM,
  fmtDateShort, isoLocal, plotConfig, seriesLabel,
} from './datasetCommon';

const DAY = 86400000;

const RANGES = [
  { key: '1d',  label: '1 d', ms: DAY },
  { key: '1w',  label: '1 w', ms: 7 * DAY },
  { key: '1m',  label: '1 m', ms: 30 * DAY },
  { key: '3m',  label: '3 m', ms: 91 * DAY },
  { key: '1y',  label: '1 y', ms: 365 * DAY },
  { key: 'all', label: 'All', ms: null },
];

const PANEL_H = 264;
const MAX_GROUPS = 5;
const MAX_SERIES_PER_REQUEST = 25;   // contract cap
const SCRUB_H = 40;
const MIN_HANDLE_PX = 28;            // a 1 d window over 40 y must stay grabbable

// Panel zoom stays available, but it is panel-local: a relayout never moves the
// shared window, and a double-click restores the axis to it.
const panelConfig = { ...plotConfig, doubleClick: 'reset' };

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const esc = (t) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const ELECTRICITY = /electric|strom|power/i;
// Default Production panel: the four saleable products, from the four plants
// that make them — not every `Output` the model happens to classify as
// production (that would drag in O₂, N₂ and the Iron Energy storage transfers).
const PRODUCT_PLANT = /electroly|ammonia|ostwald|neutralis|neutraliz/i;
const PRODUCT_RESOURCE = /hydrogen|h₂|\bh2\b|ammonia|nh3|nh₃|nitric|hno3|hno₃|ammonium nitrate|nh4no3|nh₄no₃/i;

const groupNatures = (g) => (g.natures && g.natures.length ? g.natures : NATURE_IDS);

// ---------------------------------------------------------------------------
// defaults & persistence
// ---------------------------------------------------------------------------

function defaultGroups(series) {
  const ids = (fn) => series.filter(fn).map((s) => s.id).slice(0, MAX_SERIES_PER_REQUEST);
  const batterySoc = series.filter(
    (s) => s.nature === 'storage_soc' && /batter/i.test(`${s.plant || ''} ${s.display_name || ''}`),
  ).map((s) => s.id);

  const axisRight = {};
  batterySoc.forEach((id) => { axisRight[id] = 'right'; });

  return [
    { id: 'g1', name: 'Generation',    natures: ['generation'],
      seriesIds: ids((s) => s.nature === 'generation'), axis: {}, normalise: false },
    { id: 'g2', name: 'Storage SOC',   natures: ['storage_soc'],
      seriesIds: ids((s) => s.nature === 'storage_soc'), axis: {}, normalise: true },
    { id: 'g3', name: 'Production',    natures: ['production'],
      seriesIds: ids((s) => s.nature === 'production'
        && PRODUCT_PLANT.test(s.plant || '')
        && PRODUCT_RESOURCE.test(s.resource || '')),
      axis: {}, normalise: false },
    { id: 'g4', name: 'Battery',       natures: ['battery_flow', 'storage_soc'],
      seriesIds: [...ids((s) => s.nature === 'battery_flow'), ...batterySoc].slice(0, MAX_SERIES_PER_REQUEST),
      axis: axisRight, normalise: false },
    { id: 'g5', name: 'Grid exchange', natures: ['exchange'],
      seriesIds: ids((s) => s.nature === 'exchange' && ELECTRICITY.test(s.resource || '')),
      axis: {}, normalise: false },
  ];
}

function loadPersisted(datasetId) {
  try {
    const raw = window.localStorage.getItem(`nrg.dataset.${datasetId}`);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || !Array.isArray(p.groups)) return null;
    return p;
  } catch {
    return null;
  }
}

function persist(datasetId, payload) {
  try {
    window.localStorage.setItem(`nrg.dataset.${datasetId}`, JSON.stringify(payload));
  } catch {
    /* quota or private mode — the explorer works fine without persistence */
  }
}

// ---------------------------------------------------------------------------
// data
// ---------------------------------------------------------------------------

async function fetchPoints(datasetId, seriesIds, params) {
  const ids = seriesIds.slice(0, MAX_SERIES_PER_REQUEST);
  if (!ids.length) return null;
  const q = new URLSearchParams({ series: ids.join(','), ...params });
  const res = await fetch(`${API}/api/result-datasets/${datasetId}/points?${q.toString()}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

const signatureOf = (groups) => groups.map((g) => `${g.id}:${g.seriesIds.join('.')}`).join('|');

// ---------------------------------------------------------------------------
// scrub bar
// ---------------------------------------------------------------------------

/**
 * Fixed-interval scrubber. The handle's width is proportional to the window
 * but floored at MIN_HANDLE_PX, so the pixel travel available to it is
 * `trackW - handleW` and that travel maps onto `span - winLen` of time. The
 * window length is never touched here — only its start.
 */
function ScrubBar({ tsStart, tsEnd, win, overview, onPreview, onCommit, dragging }) {
  const trackRef = useRef(null);
  const [trackW, setTrackW] = useState(0);
  const dragRef = useRef(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    setTrackW(el.clientWidth);
    if (typeof window.ResizeObserver !== 'function') return undefined;
    const ro = new window.ResizeObserver((entries) => setTrackW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const span = Math.max(1, tsEnd - tsStart);
  const winLen = Math.max(1, win.to - win.from);
  const handleW = trackW ? clamp(Math.round((winLen / span) * trackW), MIN_HANDLE_PX, trackW) : MIN_HANDLE_PX;
  const maxLeft = Math.max(0, trackW - handleW);
  const travel = Math.max(1, span - winLen);
  const left = maxLeft ? clamp(((win.from - tsStart) / travel) * maxLeft, 0, maxLeft) : 0;

  const timeAtLeft = (px) => tsStart + (clamp(px, 0, maxLeft) / (maxLeft || 1)) * travel;

  const startDrag = (e) => {
    if (!trackW) return;
    e.preventDefault();
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    const rect = trackRef.current.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - rect.left - left, from: win.from };
    onPreview(win.from);
  };

  const moveDrag = (e) => {
    if (!dragRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const from = timeAtLeft(e.clientX - rect.left - dragRef.current.dx);
    dragRef.current.from = from;
    onPreview(from);
  };

  const endDrag = (e) => {
    if (!dragRef.current) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    const { from } = dragRef.current;
    dragRef.current = null;
    onCommit(from);
  };

  // Click anywhere on the track: centre the window on the click.
  const jump = (e) => {
    if (!trackW || dragRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    onCommit(tsStart + ((e.clientX - rect.left) / trackW) * span - winLen / 2);
  };

  // Context: the coarse whole-span trace already fetched for the slider.
  const path = useMemo(() => {
    const first = overview && overview.series ? Object.values(overview.series)[0] : null;
    const v = first && first.v;
    if (!v || v.length < 2) return null;
    let min = Infinity, max = -Infinity;
    v.forEach((x) => {
      if (x === null || x === undefined || Number.isNaN(x)) return;
      if (x < min) min = x;
      if (x > max) max = x;
    });
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
    const range = max - min || 1;
    const step = 1000 / (v.length - 1);
    return v.map((x, i) => {
      const y = SCRUB_H - 1 - (((x === null || x === undefined || Number.isNaN(x) ? min : x) - min) / range) * (SCRUB_H - 2);
      return `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(2)},${y.toFixed(2)}`;
    }).join(' ');
  }, [overview]);

  return (
    <div className="ds-scrub-wrap">
      <div className="ds-scrub" ref={trackRef} onPointerDown={jump}>
        <svg className="ds-scrub-spark" viewBox={`0 0 1000 ${SCRUB_H}`} preserveAspectRatio="none" aria-hidden="true">
          {path && <path d={path} fill="none" stroke="#4ecca3" strokeWidth="1.2" opacity="0.35" />}
        </svg>
        <div
          className={`ds-scrub-handle${dragging ? ' dragging' : ''}`}
          role="slider"
          aria-label="Move the time window"
          aria-valuemin={tsStart}
          aria-valuemax={tsEnd}
          aria-valuenow={win.from}
          tabIndex={0}
          style={{ left: `${left}px`, width: `${handleW}px` }}
          onPointerDown={startDrag}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        />
      </div>
      <div className="ds-scrub-ends">
        <span>{fmtDateShort(new Date(tsStart).toISOString())}</span>
        <span>{fmtDateShort(new Date(tsEnd).toISOString())}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// trace building
// ---------------------------------------------------------------------------

function normaliseArray(arr, divisor) {
  if (!divisor) return arr;
  return arr.map((v) => (v === null || v === undefined ? null : (v / divisor) * 100));
}

function buildTraces(group, byId, detailG) {
  const traces = [];

  group.seriesIds.forEach((sid, idx) => {
    const meta = byId.get(sid);
    const key = String(sid);
    const d = detailG && detailG.series ? detailG.series[key] : null;
    if (!meta && !d) return;

    const color = PALETTE[idx % PALETTE.length];
    const yaxis = group.axis && group.axis[sid] === 'right' ? 'y2' : 'y';
    const isStock = d ? d.is_stock : !!(meta && meta.is_stock);
    const nature = (d && d.nature) || (meta && meta.nature) || 'unassigned';
    const name = (d && d.display_name) || (meta ? seriesLabel(meta) : `series ${sid}`);
    const unit = (d && d.unit) || (meta && meta.unit) || '';
    const norm = group.normalise && isStock;

    // Normalisation divisor: the peak over the loaded span, per the brief.
    let divisor = 0;
    if (norm && d) {
      const pool = (d.max && d.max.length ? d.max : d.v) || [];
      divisor = pool.reduce((m, v) => (v === null || v === undefined ? m : Math.max(m, Math.abs(v))), 0);
    }
    const scale = (arr) => (norm && divisor ? normaliseArray(arr || [], divisor) : (arr || []));
    const suffix = norm && divisor ? ' %' : (unit ? ` ${unit}` : '');

    if (!d || !d.ts || !d.ts.length) return;

    if (nature === 'battery_flow') {
      const sign = meta && meta.sign === -1 ? -1 : 1;
      traces.push({
        type: 'bar',
        x: d.ts,
        y: (d.v || []).map((v) => (v === null || v === undefined ? null : v * sign)),
        name,
        marker: { color, line: { width: 0 } },
        yaxis,
        hovertemplate: `%{x|%d %b %Y %H:%M}<br>%{y:.3f}${suffix}<extra>${name}</extra>`,
      });
      return;
    }

    if (isStock && d.min && d.max) {
      traces.push({
        type: 'scatter', mode: 'lines', x: d.ts, y: scale(d.min),
        name: `${name} min`, line: { width: 0, color },
        yaxis, showlegend: false, hoverinfo: 'skip', legendgroup: name,
      });
      traces.push({
        type: 'scatter', mode: 'lines', x: d.ts, y: scale(d.max),
        name: `${name} max`, line: { width: 0, color },
        fill: 'tonexty', fillcolor: 'rgba(255,255,255,0.10)',
        yaxis, showlegend: false, hoverinfo: 'skip', legendgroup: name,
      });
    }

    traces.push({
      type: 'scatter', mode: 'lines',
      x: d.ts, y: scale(d.v),
      name,
      legendgroup: name,
      line: { color, width: 1.4, shape: isStock ? 'linear' : 'hv' },
      yaxis,
      hovertemplate: `%{x|%d %b %Y %H:%M}<br>%{y:.3f}${suffix}<extra>${name}</extra>`,
    });
  });

  return traces;
}

// ---------------------------------------------------------------------------
// group panel
// ---------------------------------------------------------------------------

function GroupPanel({
  group, series, byId, detailG, win, datasetName, windowLabel,
  onChange, onRemove,
}) {
  const [picking, setPicking] = useState(false);
  const plotRef = useRef(null);
  const [plotW, setPlotW] = useState(0);

  useEffect(() => {
    const el = plotRef.current;
    if (!el) return undefined;
    setPlotW(el.clientWidth);
    if (typeof window.ResizeObserver !== 'function') return undefined;
    const ro = new window.ResizeObserver((entries) => setPlotW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const traces = useMemo(
    () => buildTraces(group, byId, detailG),
    [group, byId, detailG],
  );

  const hasRight = group.seriesIds.some((sid) => group.axis && group.axis[sid] === 'right');

  // Axis titles: the unit the points route reports per series, "%" when the
  // panel normalises a stock, and the distinct units joined when an axis
  // carries more than one.
  const axisUnits = useMemo(() => {
    const pick = (side) => {
      const out = [];
      group.seriesIds.forEach((sid) => {
        const onRight = !!(group.axis && group.axis[sid] === 'right');
        if ((side === 'right') !== onRight) return;
        const d = detailG && detailG.series ? detailG.series[String(sid)] : null;
        const meta = byId.get(sid);
        const isStock = d ? d.is_stock : !!(meta && meta.is_stock);
        const u = group.normalise && isStock ? '%' : ((d && d.unit) || (meta && meta.unit) || null);
        if (u && !out.includes(u)) out.push(u);
      });
      return out.join(' \u00b7 ');
    };
    return { left: pick('left'), right: pick('right') };
  }, [group.seriesIds, group.axis, group.normalise, detailG, byId]);


  const marginR = axisUnits.right ? 66 : (hasRight ? 52 : 16);
  const marginL = axisUnits.left ? 70 : 58;

  // Plotly wraps a horizontal legend but does not grow the top margin for it,
  // so a seven-series panel draws its second legend row straight across the
  // plot. Pack the entries at an over-estimated width to count the rows, then
  // buy that many rows of top margin and anchor the legend's bottom edge to
  // the top of the plot area so it grows upward into that space.
  const legendRows = useMemo(() => {
    const names = traces.filter((t) => t.showlegend !== false).map((t) => t.name || '');
    if (!names.length) return 0;
    const avail = Math.max(160, (plotW || 900) - marginL - marginR);
    let rows = 1;
    let x = 0;
    names.forEach((n) => {
      const w = 30 + n.length * 5.4;
      if (x > 0 && x + w > avail) { rows += 1; x = w; } else { x += w; }
    });
    return rows;
  }, [traces, plotW, marginL, marginR]);

  const layout = useMemo(() => ({
    paper_bgcolor: PANEL_BG,
    plot_bgcolor: PANEL_BG_PLOT,
    font: { color: PANEL_TEXT, family: 'Inter, "Work Sans", sans-serif', size: 10 },
    // The rotated axis titles need their own gutter, or Plotly clips them.
    // No rangeslider any more, so the bottom gutter is just the tick labels.
    margin: { t: 10 + legendRows * 18, r: marginR, b: 30, l: marginL },
    height: PANEL_H,
    autosize: true,
    barmode: 'relative',
    hovermode: 'x unified',
    hoverlabel: { bgcolor: PANEL_BG, bordercolor: PANEL_BORDER, font: { color: PANEL_TEXT, size: 10 } },
    // Keyed on the window so a preset click moves the axis, while a refetch
    // inside the same window leaves the user's own zoom alone.
    uirevision: `${group.id}:${win.from}:${win.to}`,
    showlegend: true,
    legend: {
      orientation: 'h', x: 0, y: 1.02, yanchor: 'bottom',
      font: { size: 9, color: PANEL_TEXT_DIM },
      bgcolor: 'rgba(0,0,0,0)',
    },
    xaxis: {
      type: 'date',
      range: [new Date(win.from).toISOString(), new Date(win.to).toISOString()],
      gridcolor: PANEL_GRID, linecolor: PANEL_BORDER,
      tickfont: { color: PANEL_TEXT_DIM, size: 9 },
      showspikes: true, spikecolor: PANEL_TEXT_DIM, spikethickness: 1, spikedash: 'dot', spikemode: 'across',
    },
    yaxis: {
      gridcolor: PANEL_GRID, linecolor: PANEL_BORDER, zeroline: true,
      zerolinecolor: 'rgba(255,255,255,0.18)',
      tickfont: { color: PANEL_TEXT_DIM, size: 9 },
      title: axisUnits.left
        ? { text: axisUnits.left, font: { size: 10, color: PANEL_TEXT_DIM }, standoff: 6 }
        : undefined,
    },
    yaxis2: {
      overlaying: 'y', side: 'right', showgrid: false,
      linecolor: PANEL_BORDER, zeroline: false,
      tickfont: { color: PANEL_TEXT_DIM, size: 9 },
      title: axisUnits.right
        ? { text: axisUnits.right, font: { size: 10, color: PANEL_TEXT_DIM }, standoff: 6 }
        : undefined,
    },
  }), [group.id, marginL, marginR, legendRows, axisUnits.left, axisUnits.right, win.from, win.to]);

  const natures = groupNatures(group);
  const pickable = useMemo(
    () => series.filter((s) => natures.includes(s.nature)),
    [series, natures],
  );

  const toggleNature = (n) => {
    const cur = group.natures || [];
    const next = cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n];
    onChange({ ...group, natures: next });
  };

  const setAxis = (sid, side) => {
    const axis = { ...(group.axis || {}) };
    if (side === 'left') delete axis[sid]; else axis[sid] = 'right';
    onChange({ ...group, axis });
  };

  const removeSeries = (sid) => {
    const axis = { ...(group.axis || {}) };
    delete axis[sid];
    onChange({ ...group, seriesIds: group.seriesIds.filter((x) => x !== sid), axis });
  };

  const overCap = group.seriesIds.length > MAX_SERIES_PER_REQUEST;

  /* Static snapshot of this group's current window in a new tab. Same
   * mechanism as FrequencyDataView.openChartFullscreen — a written document
   * carrying the Plotly bundle — but the tab is opened first, synchronously,
   * so the click's user activation still covers it. */
  const openFullscreen = () => {
    const tab = window.open('', '_blank');
    if (!tab) return;   // popup blocked

    const fs = JSON.parse(JSON.stringify(layout));
    fs.paper_bgcolor = PANEL_BG;
    fs.plot_bgcolor = PANEL_BG_PLOT;
    fs.height = undefined;
    fs.autosize = true;
    fs.margin = {
      t: 16 + Math.max(1, legendRows) * 22,
      r: axisUnits.right ? 80 : 40,
      b: 60,
      l: axisUnits.left ? 86 : 70,
    };
    fs.legend = { ...fs.legend, y: 1.02, yanchor: 'bottom', font: { size: 11, color: PANEL_TEXT_DIM } };
    delete fs.uirevision;

    const bucket = detailG && detailG.bucket ? detailG.bucket : '—';
    const title = `${group.name} · ${windowLabel}`;
    const sub = `${datasetName || 'dataset'} · ${windowLabel} · ${bucket} buckets`;

    const html = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<title>${esc(title)}</title>
<script src="https://cdn.plot.ly/plotly-2.35.0.min.js"><\/script>
<style>
  body { margin:0; background:${PANEL_BG}; overflow:hidden; color:${PANEL_TEXT};
         font-family: Inter, "Work Sans", sans-serif; }
  header { padding:10px 16px 6px; }
  h1 { margin:0; font-size:16px; font-weight:600; }
  p  { margin:2px 0 0; font-size:11px; color:${PANEL_TEXT_DIM};
       font-family: "JetBrains Mono", ui-monospace, monospace; }
  #chart { width:100vw; height:calc(100vh - 62px); }
</style>
</head><body>
<header><h1>${esc(title)}</h1><p>${esc(sub)}</p></header>
<div id="chart"></div>
<script>
  var data = ${JSON.stringify(traces)};
  var layout = ${JSON.stringify(fs)};
  Plotly.newPlot('chart', data, layout, {responsive:true, displayModeBar:true});
  window.addEventListener('resize', function() { Plotly.Plots.resize('chart'); });
<\/script>
</body></html>`;

    tab.document.open();
    tab.document.write(html);
    tab.document.close();
  };

  return (
    <div className="ds-group">
      <div className="ds-group-head">
        <input
          className="ds-input ds-input-sm ds-group-name"
          value={group.name}
          onChange={(e) => onChange({ ...group, name: e.target.value })}
        />
        <div className="ds-chip-row">
          {NATURES.map((n) => (
            <NatureChip
              key={n.id}
              nature={n.id}
              active={natures.includes(n.id) && (group.natures || []).length > 0}
              onClick={() => toggleNature(n.id)}
            />
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <button className={`toolbar-tab ${group.normalise ? 'active' : ''}`}
                title="stocks as % of their maximum over the loaded span"
                onClick={() => onChange({ ...group, normalise: !group.normalise })}>
          Normalise
        </button>
        <button className="toolbar-tab" onClick={() => setPicking((v) => !v)}>+ series</button>
        <button
          className="toolbar-tab ds-fullscreen-btn"
          title="open this group's current window in a new tab"
          onClick={openFullscreen}
          disabled={!traces.length}
        >
          ⤢ Full screen
        </button>
        <button className="ds-x" title="remove group" onClick={onRemove}>×</button>
      </div>

      {picking && (
        <div className="ds-picker">
          <SeriesTree
            series={pickable}
            selected={group.seriesIds}
            onChange={(next) => onChange({ ...group, seriesIds: next })}
            maxHeight={220}
            footer={
              <div className="ds-picker-foot">
                <span className="ds-muted">{group.seriesIds.length} selected (max {MAX_SERIES_PER_REQUEST})</span>
                <button className="toolbar-tab" onClick={() => setPicking(false)}>Done</button>
              </div>
            }
          />
        </div>
      )}

      {group.seriesIds.length > 0 && (
        <div className="ds-axis-row">
          {group.seriesIds.map((sid, idx) => {
            const meta = byId.get(sid);
            const right = group.axis && group.axis[sid] === 'right';
            return (
              <span key={sid} className="ds-pill">
                <i style={{ background: PALETTE[idx % PALETTE.length] }} />
                <span className="ds-pill-name">{meta ? seriesLabel(meta) : `series ${sid}`}</span>
                <button className={`ds-axis-btn ${right ? '' : 'on'}`} onClick={() => setAxis(sid, 'left')}>L</button>
                <button className={`ds-axis-btn ${right ? 'on' : ''}`} onClick={() => setAxis(sid, 'right')}>R</button>
                <button className="ds-x ds-x-sm" onClick={() => removeSeries(sid)}>×</button>
              </span>
            );
          })}
        </div>
      )}

      {overCap && (
        <div className="ds-error">
          {group.seriesIds.length} series selected — only the first {MAX_SERIES_PER_REQUEST} are plotted.
        </div>
      )}

      <div className="ds-plot" ref={plotRef} style={{ height: PANEL_H }}>
        {group.seriesIds.length === 0 ? (
          <div className="ds-empty" style={{ height: PANEL_H }}>No series in this group. Use “+ series”.</div>
        ) : (
          <Plot
            data={traces}
            layout={layout}
            config={panelConfig}
            style={{ width: '100%', height: PANEL_H }}
            useResizeHandler
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// tab
// ---------------------------------------------------------------------------

const DatasetExplorerTab = ({ dataset, series }) => {
  const datasetId = dataset && dataset.id;
  const tsStart = useMemo(() => new Date(dataset.ts_start).getTime(), [dataset.ts_start]);
  const tsEnd = useMemo(() => new Date(dataset.ts_end).getTime(), [dataset.ts_end]);

  const [groups, setGroups] = useState([]);
  const [preset, setPreset] = useState('1w');
  const [win, setWin] = useState({ from: tsStart, to: tsStart + 7 * DAY });
  const [detail, setDetail] = useState({});
  const [overview, setOverview] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Window start while the scrub handle is held down: labels track it live,
  // the panels and the fetch only see the committed window.
  const [dragFrom, setDragFrom] = useState(null);
  const hydrated = useRef(false);

  const byId = useMemo(() => {
    const m = new Map();
    series.forEach((s) => m.set(s.id, s));
    return m;
  }, [series]);

  // ---- hydrate from localStorage, else build the default five groups -------
  useEffect(() => {
    if (!datasetId || !series.length || hydrated.current) return;
    hydrated.current = true;
    const p = loadPersisted(datasetId);
    if (p && p.groups.length) {
      setGroups(p.groups);
      if (p.preset) setPreset(p.preset);
      if (p.win && Number.isFinite(p.win.from) && Number.isFinite(p.win.to) && p.win.to > p.win.from) {
        setWin(p.win);
        return;
      }
    } else {
      setGroups(defaultGroups(series));
    }
    setWin({ from: tsStart, to: Math.min(tsEnd, tsStart + 7 * DAY) });
  }, [datasetId, series, tsStart, tsEnd]);

  useEffect(() => { hydrated.current = false; }, [datasetId]);

  // ---- persist -------------------------------------------------------------
  useEffect(() => {
    if (!datasetId || !groups.length) return;
    persist(datasetId, { groups, win, preset });
  }, [datasetId, groups, win, preset]);

  const signature = signatureOf(groups);

  // ---- coarse whole-span trace behind the scrub track ----------------------
  useEffect(() => {
    if (!datasetId || !groups.length) return undefined;
    let cancelled = false;
    (async () => {
      const next = {};
      for (const g of groups) {
        if (!g.seriesIds.length) continue;
        try {
          const body = await fetchPoints(datasetId, g.seriesIds, { range: 'all' });
          if (body) next[g.id] = body;
        } catch {
          /* the panel still works without the slider context trace */
        }
      }
      if (!cancelled) setOverview(next);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId, signature]);

  // ---- detail traces for the current window --------------------------------
  useEffect(() => {
    if (!datasetId || !groups.length) return undefined;
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(async () => {
      const next = {};
      let err = null;
      for (const g of groups) {
        if (!g.seriesIds.length) continue;
        try {
          const body = await fetchPoints(datasetId, g.seriesIds, {
            time_from: isoLocal(new Date(win.from)),
            time_to: isoLocal(new Date(win.to)),
          });
          if (body) next[g.id] = body;
        } catch (e) {
          err = e.message || String(e);
        }
      }
      if (!cancelled) { setDetail(next); setError(err); setBusy(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); setBusy(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId, signature, win.from, win.to]);

  // ---- window controls -----------------------------------------------------
  const applyPreset = useCallback((key) => {
    setPreset(key);
    const r = RANGES.find((x) => x.key === key);
    if (!r || r.ms === null) { setWin({ from: tsStart, to: tsEnd }); return; }
    setWin((w) => {
      const from = Math.max(tsStart, Math.min(w.from, tsEnd - r.ms));
      return { from, to: Math.min(tsEnd, from + r.ms) };
    });
  }, [tsStart, tsEnd]);

  const step = useCallback((dir) => {
    setWin((w) => {
      const len = w.to - w.from;
      let from = w.from + dir * len;
      from = Math.max(tsStart, Math.min(from, tsEnd - len));
      return { from, to: from + len };
    });
  }, [tsStart, tsEnd]);

  const setCustom = (which, value) => {
    if (!value) return;
    const ms = new Date(`${value}T00:00:00`).getTime();
    if (!Number.isFinite(ms)) return;
    setPreset('custom');
    setWin((w) => (which === 'from'
      ? { from: ms, to: Math.max(ms + 3600000, w.to) }
      : { from: Math.min(w.from, ms - 3600000), to: ms }));
  };

  // Scrub: the interval is fixed, only its start moves.
  const scrubFrom = useCallback((from) => {
    const len = win.to - win.from;
    return clamp(from, tsStart, Math.max(tsStart, tsEnd - len));
  }, [win.from, win.to, tsStart, tsEnd]);

  const onScrubPreview = useCallback((from) => setDragFrom(scrubFrom(from)), [scrubFrom]);

  const onScrubCommit = useCallback((from) => {
    const len = win.to - win.from;
    const start = scrubFrom(from);
    setDragFrom(null);
    setWin({ from: start, to: start + len });
  }, [win.from, win.to, scrubFrom]);

  // ---- group management ----------------------------------------------------
  const updateGroup = useCallback((next) => {
    setGroups((gs) => gs.map((g) => (g.id === next.id ? next : g)));
  }, []);

  const removeGroup = useCallback((id) => {
    setGroups((gs) => gs.filter((g) => g.id !== id));
  }, []);

  const addGroup = useCallback(() => {
    setGroups((gs) => {
      if (gs.length >= MAX_GROUPS) return gs;
      const n = gs.reduce((m, g) => Math.max(m, Number(String(g.id).replace(/\D/g, '')) || 0), 0) + 1;
      return [...gs, { id: `g${n}`, name: `Group ${gs.length + 1}`, natures: [], seriesIds: [], axis: {}, normalise: false }];
    });
  }, []);

  const resetGroups = useCallback(() => setGroups(defaultGroups(series)), [series]);

  // ---- window caption ------------------------------------------------------
  // While the handle is held the labels follow the drag; the panels do not.
  const viewWin = dragFrom === null ? win : { from: dragFrom, to: dragFrom + (win.to - win.from) };
  const firstLoaded = Object.values(detail)[0];
  const windowLabel = `${fmtDateShort(new Date(viewWin.from).toISOString())} – ${fmtDateShort(new Date(viewWin.to).toISOString())}`;
  const caption = [
    windowLabel,
    firstLoaded && firstLoaded.points_per_series ? `${firstLoaded.points_per_series.toLocaleString()} points` : null,
    firstLoaded && firstLoaded.bucket ? `${firstLoaded.bucket} buckets` : null,
  ].filter(Boolean).join(' · ');

  // Whole-span context behind the scrub track: whichever group already has it.
  const scrubOverview = groups.map((g) => overview[g.id]).find((o) => o && o.series);

  const dateValue = (ms) => {
    const d = new Date(ms);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };

  return (
    <div className="ds-tab-body">
      <div className="climate-toolbar ds-toolbar">
        <div className="climate-toolbar-tabs">
          {RANGES.map((r) => (
            <button key={r.key}
                    className={`toolbar-tab ${preset === r.key ? 'active' : ''}`}
                    onClick={() => applyPreset(r.key)}>{r.label}</button>
          ))}
          <button className="toolbar-tab" onClick={() => step(-1)} title="previous window">‹</button>
          <button className="toolbar-tab" onClick={() => step(1)} title="next window">›</button>
        </div>
        <div className="ds-date-pickers">
          <input type="date" className="ds-input ds-input-sm"
                 value={dateValue(viewWin.from)} onChange={(e) => setCustom('from', e.target.value)} />
          <span className="ds-muted">→</span>
          <input type="date" className="ds-input ds-input-sm"
                 value={dateValue(viewWin.to)} onChange={(e) => setCustom('to', e.target.value)} />
        </div>
        <div style={{ flex: 1 }} />
        <div className="ds-muted ds-toolbar-status">
          {busy ? 'loading… ' : ''}{caption}
        </div>

        <ScrubBar
          tsStart={tsStart}
          tsEnd={tsEnd}
          win={viewWin}
          overview={scrubOverview}
          dragging={dragFrom !== null}
          onPreview={onScrubPreview}
          onCommit={onScrubCommit}
        />
      </div>

      {error && <div className="ds-error">{error}</div>}

      {groups.map((g) => (
        <GroupPanel
          key={g.id}
          group={g}
          series={series}
          byId={byId}
          detailG={detail[g.id]}
          win={win}
          datasetName={dataset.name}
          windowLabel={windowLabel}
          onChange={updateGroup}
          onRemove={() => removeGroup(g.id)}
        />
      ))}

      <div className="ds-group-actions">
        <button className="btn-ghost" onClick={addGroup} disabled={groups.length >= MAX_GROUPS}>
          + Add group {groups.length >= MAX_GROUPS ? `(max ${MAX_GROUPS})` : ''}
        </button>
        <button className="btn-ghost" onClick={resetGroups}>Reset to defaults</button>
      </div>
    </div>
  );
};

export default DatasetExplorerTab;
